import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
    Heart, Calendar, CreditCard, UtensilsCrossed, Users,
    Clock, MapPin, ChevronRight, AlertCircle, CheckCircle2,
    Loader2, Home, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function GuestPortalPage() {
    const { accessCode } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [bookingModal, setBookingModal] = useState({ open: false, activity: null });
    const [bookingLoading, setBookingLoading] = useState(false);

    useEffect(() => {
        fetchPortalData();
    }, [accessCode]);

    const fetchPortalData = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await axios.get(`${API}/family/portal/${accessCode}`);
            setData(response.data);
        } catch (err) {
            console.error('Error fetching portal:', err);
            setError(err.response?.data?.detail || 'Unable to access portal. The link may be invalid or expired.');
        } finally {
            setLoading(false);
        }
    };

    const handleBookActivity = async () => {
        if (!bookingModal.activity) return;
        
        setBookingLoading(true);
        try {
            await axios.post(`${API}/family/portal/${accessCode}/book`, {
                activity_id: bookingModal.activity.id
            });
            toast.success('Activity booked successfully!');
            setBookingModal({ open: false, activity: null });
            fetchPortalData(); // Refresh data
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to book activity');
        } finally {
            setBookingLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'Date TBC';
        return new Date(dateStr).toLocaleDateString('en-GB', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const canBook = data?.guest?.access_level === 'booking' || data?.guest?.access_level === 'full';

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F9F8F6' }}>
                <div className="text-center">
                    <Loader2 className="w-10 h-10 text-[#2D4A3E] animate-spin mx-auto mb-4" />
                    <p className="text-[#686D6A]">Loading family portal...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: '#F9F8F6' }}>
                <div className="text-center max-w-md">
                    <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                        <AlertCircle className="w-8 h-8 text-red-600" />
                    </div>
                    <h1 
                        className="text-2xl text-[#2D4A3E] mb-3"
                        style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                        Access Denied
                    </h1>
                    <p className="text-[#686D6A] mb-6">{error}</p>
                    <Button 
                        onClick={() => navigate('/')}
                        className="bg-[#2D4A3E] hover:bg-[#3A5F50]"
                    >
                        <Home className="w-4 h-4 mr-2" />
                        Go to Homepage
                    </Button>
                </div>
            </div>
        );
    }

    const { guest, resident, organization, bookings, events } = data;

    return (
        <div className="min-h-screen pb-12" style={{ backgroundColor: '#F9F8F6' }} data-testid="guest-portal-page">
            {/* Header */}
            <div className="relative overflow-hidden">
                <div 
                    className="px-6 pt-8 pb-16"
                    style={{ 
                        background: 'linear-gradient(135deg, #8B4557 0%, #A65B6C 50%, #C07080 100%)'
                    }}
                >
                    {/* Texture overlay */}
                    <div 
                        className="absolute inset-0 opacity-[0.04]"
                        style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
                        }}
                    />
                    
                    <div className="relative z-10">
                        {/* Organization name */}
                        <div className="flex items-center justify-between mb-8">
                            <span 
                                className="text-lg text-white/80"
                                style={{ fontFamily: "'Cormorant Garamond', serif" }}
                            >
                                {organization?.name || 'Residents Plus'}
                            </span>
                            <span className="px-3 py-1 rounded-full bg-white/20 text-white text-xs font-medium">
                                Family Portal
                            </span>
                        </div>
                        
                        {/* Welcome */}
                        <div className="space-y-2">
                            <p className="text-white/60 text-sm tracking-wide">Welcome,</p>
                            <h1 
                                className="text-3xl text-white"
                                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                            >
                                {guest?.name}
                            </h1>
                            <p className="text-white/70 text-sm">
                                {guest?.relationship} of {resident?.name}
                            </p>
                        </div>

                        {/* Resident Card */}
                        <div className="mt-8 bg-white/15 backdrop-blur-sm rounded-2xl p-5 border border-white/10">
                            <div className="flex items-center gap-5">
                                <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                                    <Heart className="w-7 h-7 text-white" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-white/70 text-sm tracking-wide">You're viewing</p>
                                    <p 
                                        className="text-xl text-white mt-0.5"
                                        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                                    >
                                        {resident?.name}'s Activities
                                    </p>
                                    {resident?.apartment && (
                                        <p className="text-white/60 text-sm mt-1">{resident.apartment}</p>
                                    )}
                                </div>
                                <div className="text-right">
                                    <p className="text-white/60 text-xs">Credits</p>
                                    <p 
                                        className="text-2xl text-white"
                                        style={{ fontFamily: "'Cormorant Garamond', serif" }}
                                    >
                                        {resident?.credits || 0}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Access Level Info */}
            <div className="px-5 -mt-6 relative z-10">
                <div className={`rounded-2xl p-4 border ${
                    canBook 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-amber-50 border-amber-200'
                }`}>
                    <div className="flex items-center gap-3">
                        {canBook ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                        ) : (
                            <AlertCircle className="w-5 h-5 text-amber-600" />
                        )}
                        <div>
                            <p className={`font-medium text-sm ${canBook ? 'text-green-800' : 'text-amber-800'}`}>
                                {guest?.access_level?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} Access
                            </p>
                            <p className={`text-xs ${canBook ? 'text-green-600' : 'text-amber-600'}`}>
                                {canBook 
                                    ? 'You can book activities on behalf of ' + resident?.name
                                    : 'You can view activities but cannot make bookings'
                                }
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Current Bookings */}
            <div className="px-5 mt-8">
                <h2 
                    className="text-xl text-[#2D4A3E] mb-4"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                >
                    {resident?.name}'s Bookings
                </h2>
                
                {bookings?.length > 0 ? (
                    <div className="space-y-3">
                        {bookings.map((booking) => (
                            <div 
                                key={booking.id}
                                className="bg-white rounded-2xl p-4 border border-[#E5E3DF] flex items-center gap-4"
                            >
                                <div className="w-12 h-12 rounded-xl bg-[#8A9A86]/10 flex items-center justify-center flex-shrink-0">
                                    <Calendar className="w-6 h-6 text-[#8A9A86]" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-medium text-[#2D4A3E] truncate">
                                        {booking.activity?.title || 'Activity'}
                                    </h3>
                                    <p className="text-sm text-[#686D6A] mt-0.5">
                                        {booking.activity?.date_time ? formatDate(booking.activity.date_time) : 'Date TBC'}
                                    </p>
                                    {booking.activity?.location && (
                                        <p className="text-xs text-[#8A9A86] flex items-center gap-1 mt-1">
                                            <MapPin className="w-3 h-3" />
                                            {booking.activity.location}
                                        </p>
                                    )}
                                </div>
                                <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                                    Confirmed
                                </span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl p-6 border border-[#E5E3DF] text-center">
                        <Calendar className="w-10 h-10 text-[#E5E3DF] mx-auto mb-3" />
                        <p className="text-[#686D6A]">No upcoming bookings</p>
                    </div>
                )}
            </div>

            {/* Upcoming Events */}
            <div className="px-5 mt-8">
                <div className="flex items-center justify-between mb-4">
                    <h2 
                        className="text-xl text-[#2D4A3E]"
                        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                    >
                        Upcoming Events
                    </h2>
                    {canBook && (
                        <span className="text-xs text-[#8A9A86]">Tap to book</span>
                    )}
                </div>
                
                {events?.length > 0 ? (
                    <div className="space-y-3">
                        {events.map((event) => (
                            <div 
                                key={event.id}
                                className={`bg-white rounded-2xl p-4 border border-[#E5E3DF] ${
                                    canBook ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200' : ''
                                }`}
                                onClick={() => canBook && setBookingModal({ open: true, activity: event })}
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                                        <Calendar className="w-6 h-6 text-amber-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-medium text-[#2D4A3E] truncate">
                                            {event.title}
                                        </h3>
                                        <p className="text-sm text-[#686D6A] mt-0.5">
                                            {event.start_date_time ? formatDate(event.start_date_time) : 'Date TBC'}
                                        </p>
                                        {event.credits_required && (
                                            <p className="text-xs text-[#8A9A86] flex items-center gap-1 mt-1">
                                                <CreditCard className="w-3 h-3" />
                                                {event.credits_required} credit{event.credits_required > 1 ? 's' : ''}
                                            </p>
                                        )}
                                    </div>
                                    {canBook && (
                                        <ChevronRight className="w-5 h-5 text-[#8A9A86]" />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl p-6 border border-[#E5E3DF] text-center">
                        <Calendar className="w-10 h-10 text-[#E5E3DF] mx-auto mb-3" />
                        <p className="text-[#686D6A]">No upcoming events</p>
                    </div>
                )}
            </div>

            {/* Info Footer */}
            <div className="px-5 mt-8">
                <div className="bg-[#2D4A3E]/5 rounded-2xl p-5">
                    <h3 className="font-medium text-[#2D4A3E] mb-2">About This Portal</h3>
                    <p className="text-sm text-[#686D6A] leading-relaxed">
                        This portal lets you stay connected with {resident?.name}'s activities at {organization?.name}. 
                        {canBook 
                            ? ` You can view and book activities on their behalf using their available credits.`
                            : ` Contact ${resident?.name} if you'd like to upgrade your access to make bookings.`
                        }
                    </p>
                </div>
            </div>

            {/* Booking Confirmation Modal */}
            <Dialog open={bookingModal.open} onOpenChange={(open) => !open && setBookingModal({ open: false, activity: null })}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Confirm Booking</DialogTitle>
                        <DialogDescription>
                            Book this activity for {resident?.name}
                        </DialogDescription>
                    </DialogHeader>
                    {bookingModal.activity && (
                        <div className="space-y-4">
                            <div className="bg-[#F9F8F6] rounded-xl p-4">
                                <h3 className="font-semibold text-[#2D4A3E]">{bookingModal.activity.title}</h3>
                                <p className="text-sm text-[#686D6A] mt-1">
                                    {bookingModal.activity.start_date_time 
                                        ? formatDate(bookingModal.activity.start_date_time) 
                                        : 'Date TBC'
                                    }
                                </p>
                                {bookingModal.activity.credits_required && (
                                    <p className="text-sm text-[#8A9A86] mt-2">
                                        Cost: {bookingModal.activity.credits_required} credit{bookingModal.activity.credits_required > 1 ? 's' : ''}
                                    </p>
                                )}
                            </div>
                            
                            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                                <span className="text-sm text-amber-800">
                                    {resident?.name}'s available credits
                                </span>
                                <span className="font-semibold text-amber-800">
                                    {resident?.credits || 0}
                                </span>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button 
                            variant="outline" 
                            onClick={() => setBookingModal({ open: false, activity: null })}
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleBookActivity}
                            disabled={bookingLoading}
                            className="bg-[#2D4A3E] hover:bg-[#3A5F50]"
                            data-testid="confirm-booking-btn"
                        >
                            {bookingLoading ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <CheckCircle2 className="w-4 h-4 mr-2" />
                            )}
                            Confirm Booking
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default GuestPortalPage;
